import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from '../../http';

function passRateColor(rate) {
  if (rate == null) return { bg: '#edf2f7', color: '#718096' };
  if (rate >= 80) return { bg: '#c6f6d5', color: '#22543d' };
  if (rate >= 60) return { bg: '#fefcbf', color: '#744210' };
  return { bg: '#fed7d7', color: '#822727' };
}

export default function AggregateView() {
  const [searchParams] = useSearchParams();
  const eventIdsParam = searchParams.get('event_ids');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const url = eventIdsParam
      ? `/api/reports/aggregate?event_ids=${eventIdsParam}`
      : '/api/reports/aggregate?status=all';
    axios
      .get(url)
      .then((res) => {
        setData(res.data);
      })
      .catch(() => setError('Failed to load aggregate data.'))
      .finally(() => setLoading(false));
  }, [eventIdsParam]);

  if (loading) return <div style={{ padding: '2rem' }}>Loading aggregate data...</div>;
  if (error) return <div style={{ padding: '2rem', color: '#c53030' }}>{error}</div>;
  if (!data || !data.events.length)
    return (
      <div style={{ padding: '2rem' }}>
        <Link to="/admin/reports" className="btn btn-secondary" style={{ marginBottom: '1.5rem' }}>
          ← Back to Reports
        </Link>
        <p style={{ color: '#a0aec0' }}>No completed event data available yet.</p>
      </div>
    );

  const { events, aggregate, by_station } = data;

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Aggregate Report</h1>
          <p style={{ color: '#718096', margin: '0.25rem 0 0' }}>
            Pass rates across {events.length} test event{events.length !== 1 ? 's' : ''}.
          </p>
        </div>
        <Link to="/admin/reports" className="btn btn-secondary">
          ← Back to Reports
        </Link>
      </div>

      {/* Summary row */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        {[
          { label: 'Events', value: events.length },
          { label: 'Companies', value: aggregate.total_companies },
          { label: 'Station Sessions', value: aggregate.total_sessions },
          {
            label: 'Overall Pass Rate',
            value: aggregate.overall_pass_rate !== null ? `${aggregate.overall_pass_rate}%` : '—',
          },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '1rem 1.25rem',
              flex: '1 1 130px',
            }}
          >
            <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{s.value}</div>
            <div style={{ fontSize: '0.85rem', color: '#718096' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Station × Event matrix */}
      {by_station.length === 0 ? (
        <p style={{ color: '#a0aec0' }}>No station data available.</p>
      ) : (
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
            <thead style={{ background: '#f7fafc', borderBottom: '2px solid #edf2f7' }}>
              <tr>
                <th
                  style={{
                    padding: '0.75rem 1rem',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#4a5568',
                    position: 'sticky',
                    left: 0,
                    background: '#f7fafc',
                    zIndex: 10,
                  }}
                >
                  Station
                </th>
                {events.map((ev) => (
                  <th
                    key={ev.id}
                    style={{
                      padding: '0.75rem 0.5rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: '#4a5568',
                      fontSize: '0.8rem',
                      minWidth: '110px',
                    }}
                  >
                    <div>{ev.name}</div>
                    <div style={{ fontWeight: 400, color: '#718096' }}>
                      {new Date(ev.event_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {by_station.map((station) => {
                const evMap = Object.fromEntries(
                  station.events.map((e) => [e.event_id, e])
                );
                return (
                  <tr key={station.station_name} style={{ borderBottom: '1px solid #edf2f7' }}>
                    <td
                      style={{
                        padding: '0.75rem 1rem',
                        fontWeight: 500,
                        position: 'sticky',
                        left: 0,
                        background: 'white',
                        borderRight: '1px solid #edf2f7',
                        zIndex: 5,
                      }}
                    >
                      {station.station_number ? `${station.station_number}. ` : ''}
                      {station.station_name}
                    </td>
                    {events.map((ev) => {
                      const cell = evMap[ev.id];
                      const { bg, color } = passRateColor(cell?.pass_rate ?? null);
                      return (
                        <td
                          key={ev.id}
                          style={{
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            background: bg,
                            color,
                            fontWeight: 600,
                          }}
                        >
                          {cell ? (
                            <div>
                              <div style={{ fontSize: '1rem' }}>{cell.pass_rate}%</div>
                              <div style={{ fontSize: '0.75rem', fontWeight: 400, opacity: 0.8 }}>
                                {cell.passed_sessions}/{cell.total_sessions}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e0' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
